import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiHome, FiShoppingBag, FiTag, FiBookOpen, FiCalendar,
  FiSettings, FiLogOut, FiMenu, FiX, FiUser, FiFileText,
  FiLayout, FiNavigation, FiGrid, FiHeart, FiBook, FiChevronDown,
  FiMessageSquare, FiStar,
} from "react-icons/fi";

const navGroups = [
  {
    label: "Main",
    items: [
      { label: "Dashboard", icon: <FiHome />, path: "/admin/dashboard" },
    ],
  },
  {
    label: "Shop",
    items: [
      { label: "Products", icon: <FiShoppingBag />, path: "/admin/products" },
      { label: "Product Categories", icon: <FiTag />, path: "/admin/product-categories" },
      { label: "Reviews", icon: <FiStar />, path: "/admin/reviews" },
    ],
  },
  {
    label: "Content",
    items: [
      { label: "Home", icon: <FiGrid />, path: "/admin/home" },
      { label: "About", icon: <FiFileText />, path: "/admin/about" },
      { label: "Self-Care", icon: <FiHeart />, path: "/admin/self-care" },
      { label: "Author's Corner", icon: <FiBook />, path: "/admin/author-corner" },
      { label: "Mental Health Resources", icon: <FiHeart />, path: "/admin/mental-health" },
      { label: "Blog Posts", icon: <FiBookOpen />, path: "/admin/blogs" },
      { label: "Blog Categories", icon: <FiFileText />, path: "/admin/blog-categories" },
      { label: "Events", icon: <FiCalendar />, path: "/admin/events" },
      { label: "Event Registrations", icon: <FiUser />, path: "/admin/event-registrations" },
      { label: "Pages", icon: <FiLayout />, path: "/admin/pages" },
    ],
  },
  {
    label: "Books",
    items: [
      { label: "Book Store", icon: <FiBook />, path: "/admin/book-store" },
      { label: "E-Books", icon: <FiBookOpen />, path: "/admin/e-books" },
    ],
  },
  {
    label: "Forms",
    items: [
      { label: "Contact Forms", icon: <FiMessageSquare />, path: "/admin/contact-forms" },
    ],
  },
  {
    label: "Site",
    items: [
      { label: "Navigation", icon: <FiNavigation />, path: "/admin/navigation" },
      { label: "Site Settings", icon: <FiSettings />, path: "/admin/settings" },
      { label: "Profile", icon: <FiUser />, path: "/admin/profile" },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className="min-h-screen bg-gray-50 flex" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "w-60" : "w-16"} bg-white border-r border-gray-100 flex flex-col transition-all duration-300 shrink-0 fixed h-full z-30 shadow-sm`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#e84fa5] rounded-lg flex items-center justify-center">
                <span className="text-white text-[11px] font-extrabold">SK</span>
              </div>
              <span className="font-extrabold text-[#e84fa5] text-[14px]">Admin Panel</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-gray-700 p-1 rounded">
            {sidebarOpen ? <FiX size={18} /> : <FiMenu size={18} />}
          </button>
        </div>

        {/* Nav Groups */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-2">
              {sidebarOpen && (
                <p className="px-4 py-1.5 text-[10px] font-extrabold text-gray-300 uppercase tracking-widest">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold transition mx-2 rounded-lg mb-0.5 ${
                      active ? "bg-pink-50 text-[#e84fa5]" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <span className={`text-[15px] shrink-0 ${active ? "text-[#e84fa5]" : ""}`}>{item.icon}</span>
                    {sidebarOpen && <span className="truncate">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User Footer */}
        <div className="border-t border-gray-100 p-3">
          {sidebarOpen && currentUser && (
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center shrink-0">
                <FiUser size={14} className="text-[#e84fa5]" />
              </div>
              <p className="text-[11px] text-gray-500 truncate">{currentUser.email}</p>
            </div>
          )}
          <button onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-[13px] text-red-500 hover:bg-red-50 font-semibold transition rounded-lg w-full"
            title="Logout"
          >
            <FiLogOut size={15} />
            {sidebarOpen && "Logout"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 ${sidebarOpen ? "ml-60" : "ml-16"} transition-all duration-300 min-h-screen`}>
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
